
import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Navigation, Home, Clock, Send, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { addressSharingService } from "@/services/addressSharingService";
import RouteTrackingWidget from "./RouteTrackingWidget";
import HostRouteViewer from "./HostRouteViewer";
import ReceivedAddressWidget from "./ReceivedAddressWidget";

interface AddressRibbonProps {
  otherUser: any;
  matchId?: string;
}

const AddressRibbon = ({ otherUser, matchId }: AddressRibbonProps) => {
  const { user } = useAuth();
  const [activeWidget, setActiveWidget] = useState<'route' | 'host-viewer' | null>(null);
  
  // State to store destination address for route tracking
  const [destinationAddress, setDestinationAddress] = useState<string>("");
  const [sharedAddresses, setSharedAddresses] = useState<any[]>([]);
  
  // Address sharing state
  const [isAddressSharing, setIsAddressSharing] = useState(false);
  const [address, setAddress] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [isSharing, setIsSharing] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [sharedAddressId, setSharedAddressId] = useState<string | null>(null);

  const getPartnerDisplayName = () => {
    if (!otherUser) return "User";
    
    if (otherUser.display_name) {
      return otherUser.display_name;
    }
    
    const fullName = `${otherUser.first_name || ""} ${otherUser.last_name || ""}`.trim();
    return fullName || "User";
  };

  // Load user's saved address and shared addresses when component mounts
  useEffect(() => {
    const loadUserAddress = async () => {
      if (!user?.id) return;

      try {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('address, city, additional_info')
          .eq('user_id', user.id)
          .single();

        if (error || !profile) return;

        // Combine address and city
        const fullAddress = [profile.address, profile.city]
          .filter(Boolean)
          .join(', ');

        if (fullAddress) {
          setAddress(fullAddress);
          setDestinationAddress(fullAddress); // Set as destination for route tracking
        }

        if (profile.additional_info) {
          setAdditionalInfo(profile.additional_info);
        }
      } catch (error) {
        console.error('Error loading user address:', error);
      }
    };

    const loadSharedAddresses = async () => {
      if (!user?.id) return;

      try {
        const addresses = await addressSharingService.getSharedAddresses(user.id);
        setSharedAddresses(addresses);
        
        // If there are shared addresses, use the first one as destination
        if (addresses.length > 0) {
          setDestinationAddress(addresses[0].address);
        }
      } catch (error) {
        console.error('Error loading shared addresses:', error);
      }
    };

    loadUserAddress();
    loadSharedAddresses();
  }, [user?.id]);

  // Timer countdown effect
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev === null || prev <= 1) {
          setIsSharing(false);
          setSharedAddressId(null);
          setIsAddressSharing(false);
          toast({
            title: "Address Sharing Expired",
            description: "Your address is no longer being shared",
          });
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleShareAddress = () => {
    if (isSharing) {
      // If currently sharing, stop sharing
      handleStopSharing();
    } else {
      // Toggle address sharing form
      setIsAddressSharing(!isAddressSharing);
    }
  };

  const handleShareLocation = () => {
    setActiveWidget(activeWidget === 'route' ? null : 'route');
  };

  const handleViewRoutes = () => {
    setActiveWidget(activeWidget === 'host-viewer' ? null : 'host-viewer');
  };

  const handleCloseWidget = () => {
    setActiveWidget(null);
  };

  const handleShare = async () => {
    if (!address.trim()) {
      toast({
        title: "Address Required",
        description: "Please enter an address to share",
        variant: "destructive",
      });
      return;
    }

    if (!user?.id) return;

    try {
      const shareData = await addressSharingService.shareAddress({
        senderId: user.id,
        receiverId: otherUser.user_id,
        address: address.trim(),
        additionalInfo: additionalInfo.trim() || null,
      });

      setSharedAddressId(shareData.id);
      setIsSharing(true);
      setTimeLeft(3600); // 60 minutes in seconds
      setDestinationAddress(address.trim()); // Set destination for route tracking
      
      // Update shared addresses list
      const updatedAddresses = await addressSharingService.getSharedAddresses(user.id);
      setSharedAddresses(updatedAddresses);
    } catch (error) {
      console.error('Error sharing address:', error);
      toast({
        title: "Error",
        description: "Failed to share address. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleStopSharing = async () => {
    if (!sharedAddressId) return;

    try {
      await addressSharingService.stopSharingAddress(sharedAddressId);
      setIsSharing(false);
      setTimeLeft(null);
      setSharedAddressId(null);
      setIsAddressSharing(false);
    } catch (error) {
      console.error('Error stopping address sharing:', error);
      toast({
        title: "Error",
        description: "Failed to stop sharing. Please try again.",
        variant: "destructive",
      });
    }
  };

  const renderHostBox = () => (
    <Card className="dark-pod border-neon-red bg-carbon/80 backdrop-blur-glass">
      <CardContent className="p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Home className="w-6 h-6 text-neon-red" />
          <h3 className="text-xl font-semibold text-ice-white">I'm the Host</h3>
        </div>
        
        {!isAddressSharing && !isSharing && (
          <Button 
            onClick={handleShareAddress} 
            className="w-full bg-neon-red hover:bg-neon-red/80 text-ice-white font-semibold py-3 px-6 rounded-lg shadow-neon-red hover:shadow-lg hover:scale-105 transition-all duration-300"
          >
            <MapPin className="w-5 h-5 mr-2" />
            Click here to share address
          </Button>
        )}

        {isAddressSharing && !isSharing && (
          <div className="space-y-4">
            <div className="text-sm text-ice-white mb-3">
              Sharing with {getPartnerDisplayName()}:
            </div>
            
            <div>
              <Input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter your address"
                className="bg-carbon border-ice-white/20 text-ice-white placeholder:text-ice-white/50"
              />
            </div>

            <div>
              <Textarea
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                placeholder="Additional information (entry details, floor, etc.)"
                rows={2}
                className="bg-carbon border-ice-white/20 text-ice-white placeholder:text-ice-white/50"
              />
            </div>

            <Button
              onClick={handleShare}
              className="w-full bg-neon-red hover:bg-neon-red/80 text-ice-white font-semibold"
              disabled={!address.trim()}
            >
              <Send className="w-4 h-4 mr-2" />
              Share Address
            </Button>
          </div>
        )}

        {isSharing && timeLeft && (
          <div className="space-y-4">
            <div className="text-sm text-ice-white mb-3">
              Sharing with {getPartnerDisplayName()}:
            </div>
            
            <div className="text-sm text-electric-violet flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{formatTime(timeLeft)}</span>
            </div>

            <Button
              onClick={handleStopSharing}
              variant="destructive"
              className="w-full"
            >
              <X className="w-4 h-4 mr-2" />
              Stop sharing
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderGuestBox = () => (
    <Card className="dark-pod border-electric-violet bg-carbon/80 backdrop-blur-glass">
      <CardContent className="p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Navigation className="w-6 h-6 text-electric-violet" />
          <h3 className="text-xl font-semibold text-ice-white">I'm the Guest</h3>
        </div>
        
        <Button 
          onClick={handleShareLocation} 
          className="w-full bg-neon-red hover:bg-neon-red/80 text-ice-white font-semibold py-3 px-6 rounded-lg shadow-neon-red hover:shadow-lg hover:scale-105 transition-all duration-300"
        >
          <Navigation className="w-5 h-5 mr-2" />
          Click here to share live location
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-4 space-y-4 black-background pb-24">
      {/* Received Addresses */}
      <ReceivedAddressWidget otherUserId={otherUser?.user_id} />
      
      <div className="space-y-4">
        {renderHostBox()}
        {renderGuestBox()}
        
        {/* Host Route Viewer Button */}
        {sharedAddresses.length > 0 && (
          <Card className="dark-pod border-neon-red bg-carbon/80 backdrop-blur-glass">
            <CardContent className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Navigation className="w-6 h-6 text-neon-red" />
                <h3 className="text-xl font-semibold text-ice-white">Track Guest Routes</h3>
              </div>
              
              <Button 
                onClick={handleViewRoutes} 
                className="w-full bg-neon-red hover:bg-neon-red/80 text-ice-white font-semibold py-3 px-6 rounded-lg shadow-neon-red hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <Navigation className="w-5 h-5 mr-2" />
                View guest routes to your address
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Active Widgets */}
      {activeWidget === 'route' && otherUser?.user_id && (
        <RouteTrackingWidget
          hostUserId={otherUser.user_id}
          hostUserName={getPartnerDisplayName()}
          destinationAddress={destinationAddress || "Destination address"}
          addressShareId={sharedAddressId || undefined}
          onClose={handleCloseWidget}
        />
      )}
      
      {activeWidget === 'host-viewer' && (
        <HostRouteViewer
          onClose={handleCloseWidget}
        />
      )}
    </div>
  );
};

export default AddressRibbon;
